import { supabase } from './supabase';

export interface DiseaseProfile {
  id: string;
  user_id: string;
  disease_type: string | null;
  diagnosis_date: string | null;
  diagnosed_by: string | null;
  disease_location: string[];
  disease_location_other: string | null;
  last_flare_date: string | null;
  last_colonoscopy_date: string | null;
  avg_bowel_movements_remission: number | null;
  has_stoma: boolean | null;
  bowel_surgeries: boolean | null;
  bowel_surgeries_description: string | null;
  food_allergies: string | null;
  diet: string[];
  diet_other: string | null;
  doctor_name: string | null;
  hospital: string | null;
  created_at: string;
  updated_at: string;
}

const toDbFormat = (data: Partial<DiseaseProfile>) => ({
  disease_type: data.disease_type ?? null,
  diagnosis_date: data.diagnosis_date || null,
  diagnosed_by: data.diagnosed_by || null,
  disease_location: data.disease_location ?? [],
  disease_location_other: data.disease_location_other || null,
  last_flare_date: data.last_flare_date || null,
  last_colonoscopy_date: data.last_colonoscopy_date || null,
  avg_bowel_movements_remission: data.avg_bowel_movements_remission ?? null,
  has_stoma: data.has_stoma ?? null,
  bowel_surgeries: data.bowel_surgeries ?? null,
  bowel_surgeries_description: data.bowel_surgeries_description || null,
  food_allergies: data.food_allergies || null,
  diet: data.diet ?? [],
  diet_other: data.diet_other || null,
  doctor_name: data.doctor_name || null,
  hospital: data.hospital || null,
  updated_at: new Date().toISOString()
});

export const getDiseaseProfile = async (userId: string): Promise<DiseaseProfile | null> => {
  const { data, error } = await supabase
    .from('disease_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Błąd pobierania disease_profiles:', error);
    return null;
  }
  if (!data) return null;
  return mapFromDb(data);
};

const mapFromDb = (row: any): DiseaseProfile => ({
  id: row.id,
  user_id: row.user_id,
  disease_type: row.disease_type,
  diagnosis_date: row.diagnosis_date,
  diagnosed_by: row.diagnosed_by,
  disease_location: Array.isArray(row.disease_location) ? row.disease_location : [],
  disease_location_other: row.disease_location_other,
  last_flare_date: row.last_flare_date,
  last_colonoscopy_date: row.last_colonoscopy_date,
  avg_bowel_movements_remission: row.avg_bowel_movements_remission,
  has_stoma: row.has_stoma,
  bowel_surgeries: row.bowel_surgeries,
  bowel_surgeries_description: row.bowel_surgeries_description,
  food_allergies: row.food_allergies,
  diet: Array.isArray(row.diet) ? row.diet : [],
  diet_other: row.diet_other,
  doctor_name: row.doctor_name,
  hospital: row.hospital,
  created_at: row.created_at,
  updated_at: row.updated_at
});

export const upsertDiseaseProfile = async (userId: string, data: Partial<DiseaseProfile>): Promise<DiseaseProfile | null> => {
  const payload = toDbFormat(data);
  const existing = await getDiseaseProfile(userId);

  if (existing) {
    const { data: result, error } = await supabase
      .from('disease_profiles')
      .update(payload)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Błąd aktualizacji disease_profiles:', error);
      throw new Error(error.message);
    }
    return result ? mapFromDb(result) : null;
  }

  const { data: result, error } = await supabase
    .from('disease_profiles')
    .insert({ user_id: userId, ...payload })
    .select()
    .single();

  if (error) {
    console.error('Błąd wstawiania disease_profiles:', error);
    throw new Error(error.message);
  }
  return result ? mapFromDb(result) : null;
};
